import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getMessagingIfSupported, getVapidKey } from '../lib/firebaseClient'
import { getToken as getFcmToken, onMessage, deleteToken as deleteFcmToken } from 'firebase/messaging'
import { supabase } from '../lib/supabase'
import toast from '../utils/toast'
import { useAuth } from '../contexts/AuthContext'

interface UsePushReturn {
  supported: boolean
  permission: NotificationPermission
  token: string | null
  loading: boolean
  error: string | null
  requestAndSaveToken: () => Promise<void>
  removeToken: () => Promise<void>
}

export function usePushNotifications(): UsePushReturn {
  const { user } = useAuth()
  const [supported, setSupported] = useState<boolean>(false)
  const [permission, setPermission] = useState<NotificationPermission>(Notification.permission)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Detecta suporte a FCM
  useEffect(() => {
    let mounted = true
    ;(async () => {
      const messaging = await getMessagingIfSupported()
      if (!mounted) return
      setSupported(Boolean(messaging))
    })()
    return () => {
      mounted = false
    }
  }, [])

  // Obtém token já salvo na inicialização (evitar repetição)
  const fetchedOnceRef = useRef(false)
  useEffect(() => {
    let active = true
    ;(async () => {
      const userId = user?.id
      if (!userId) {
        fetchedOnceRef.current = false
        if (active) setToken(null)
        return
      }
      if (fetchedOnceRef.current) return
      // Evitar chamadas quando offline
      try {
        if (typeof navigator !== 'undefined' && 'onLine' in navigator && navigator.onLine === false) return
      } catch { /* ignore */ }
      fetchedOnceRef.current = true
      const { data, error } = await supabase
        .from('push_tokens')
        .select('token')
        .eq('user_id', userId)
        .limit(1)
      if (!active) return
      if (error) {
        console.error('[usePushNotifications] Falha ao carregar token salvo:', error)
        return
      }
      setToken(data?.[0]?.token ?? null)
    })()
    return () => {
      active = false
    }
  }, [user?.id])

  // Listener para mensagens em primeiro plano
  useEffect(() => {
    let unsub: (() => void) | undefined
    ;(async () => {
      const messaging = await getMessagingIfSupported()
      if (!messaging) return
      unsub = onMessage(messaging, (payload) => {
        const title = (payload.notification?.title || 'Notificação') as string
        const body = payload.notification?.body as string | undefined
        toast.info(body ? `${title}: ${body}` : title)
      })
    })()
    return () => {
      if (unsub) unsub()
    }
  }, [])

  const getRegistration = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    if (!('serviceWorker' in navigator)) return null
    try {
      // Aguarda o SW atual
      return await navigator.serviceWorker.ready
    } catch {
      return null
    }
  }, [])

  const requestAndSaveToken = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      if (!supported) {
        toast.error('Notificações push não são suportadas neste navegador.')
        return
      }
      if (!user) {
        toast.error('É necessário estar logado para ativar notificações.')
        return
      }

      // Solicita permissão se necessário
      let current = Notification.permission
      if (current === 'default') {
        current = await Notification.requestPermission()
      }
      setPermission(current)
      if (current !== 'granted') {
        toast.error('Permissão de notificação negada.')
        return
      }

      const messaging = await getMessagingIfSupported()
      const registration = await getRegistration()
      const vapidKey = getVapidKey()

      if (!messaging || !registration || !vapidKey) {
        toast.error('Falha ao inicializar push. Verifique VAPID key e Service Worker.')
        return
      }

      const newToken = await getFcmToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      })

      if (!newToken) {
        toast.error('Não foi possível obter o token de push.')
        return
      }

      setToken(newToken)

      // Salvar/atualizar no Supabase
      const { error: upsertErr } = await supabase
        .from('push_tokens')
        .upsert({ user_id: user.id, token: newToken, platform: 'web' }, { onConflict: 'token' })

      if (upsertErr) throw upsertErr

      toast.success('Notificações ativadas!')
    } catch (e: any) {
      console.error('[usePushNotifications] Erro ao registrar push:', e)
      setError(e?.message || 'Erro ao ativar notificações')
      toast.error('Erro ao ativar notificações')
    } finally {
      setLoading(false)
    }
  }, [supported, user, getRegistration])

  const removeToken = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const messaging = await getMessagingIfSupported()
      await getRegistration() // garante que o SW esteja pronto, mesmo que não seja usado diretamente
      if (!messaging || !token) {
        toast.error('Nenhum token de push ativo para remover.')
        return
      }

      const deleted = await deleteFcmToken(messaging)
      if (!deleted) {
        toast.error('Não foi possível remover o token de push.')
        return
      }

      if (user) {
        await supabase.from('push_tokens').delete().eq('user_id', user.id)
      }
      setToken(null)
      toast.success('Notificações desativadas.')
    } catch (e: any) {
      console.error('[usePushNotifications] Erro ao remover token:', e)
      setError(e?.message || 'Erro ao desativar notificações')
      toast.error('Erro ao desativar notificações')
    } finally {
      setLoading(false)
    }
  }, [token, user, getRegistration])

  return useMemo(
    () => ({ supported, permission, token, loading, error, requestAndSaveToken, removeToken }),
    [supported, permission, token, loading, error, requestAndSaveToken, removeToken]
  )
}


